import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";

import { ApiError, api, type ConnectorSummary } from "../lib/api";
import { useWS } from "../lib/ws";
import { AddSourceDialog } from "../components/AddSourceDialog";
import { ConfigureConnectorModal } from "../components/plc/ConfigureConnectorModal";
import { EditConnectorModal } from "../components/plc/EditConnectorModal";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { useToast } from "../components/toast/ToastProvider";
import { PlatformLogo } from "../components/PlatformLogo";
import { cn } from "../lib/cn";

interface SourceRow {
  id: string;
  name: string;
  connectorId: string;
  config: Record<string, unknown>;
  structuredProperties?: Record<string, unknown>;
  scheduleCron: string;
  enabled: boolean;
  includeRawAttachments: boolean;
  lastStatus?: string;
  lastRunId?: string;
  createdAt: string;
}

interface Endpoint {
  id: string;
  kind: string;
  enabled: boolean;
}

interface BuildStatus {
  state: "building" | "done" | "failed";
  artifactId?: string;
  error?: string;
}

type BuildPhase = "idle" | "building" | "done" | "failed";
type StatusFilter = "all" | "never" | "succeeded" | "failed" | "running" | "cancelled";

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All statuses" },
  { value: "never", label: "Never run" },
  { value: "succeeded", label: "Succeeded" },
  { value: "failed", label: "Failed" },
  { value: "running", label: "Running" },
  { value: "cancelled", label: "Cancelled" },
];

export function SourcesPage() {
  const qc = useQueryClient();
  const { show } = useToast();
  const [, setLocation] = useLocation();
  const [adding, setAdding] = useState(false);
  const [configuringConnector, setConfiguringConnector] = useState<ConnectorSummary | null>(null);
  const [editing, setEditing] = useState<SourceRow | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [confirmDelete, setConfirmDelete] = useState<SourceRow | null>(null);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [buildPhase, setBuildPhase] = useState<BuildPhase>("idle");
  const [buildId, setBuildId] = useState<string | null>(null);
  const [_builtArtifactId, setBuiltArtifactId] = useState<string | null>(null);
  const [buildError, setBuildError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const sources = useQuery({
    queryKey: ["sources"],
    queryFn: () => api.get<SourceRow[]>("/api/v1/sources"),
  });

  const endpoints = useQuery({
    queryKey: ["endpoints"],
    queryFn: () => api.get<Endpoint[]>("/api/v1/endpoints"),
  });
  const enabledEndpoint = endpoints.data?.find((e) => e.enabled);
  const isLcaMode = !enabledEndpoint || enabledEndpoint.kind !== "catalog-direct";

  let lastInvalidate = 0;
  useWS({
    onEvent: (ev) => {
      if (ev.kind !== "crawl.status") return;
      const now = Date.now();
      if (now - lastInvalidate < 500) return;
      lastInvalidate = now;
      qc.invalidateQueries({ queryKey: ["sources"] });
    },
  });

  const refresh = useMutation({
    mutationFn: (id: string) =>
      api.post<{ runId: string }>(`/api/v1/sources/${encodeURIComponent(id)}/refresh`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sources"] }),
    onError: (err) => {
      const msg = err instanceof Error ? err.message : "Refresh failed to start";
      show("Refresh failed", msg, "error");
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/sources/${encodeURIComponent(id)}`),
    onSuccess: (_, id) => {
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      qc.invalidateQueries({ queryKey: ["sources"] });
    },
  });

  const buildArtifact = useMutation({
    mutationFn: (sourceIds: string[]) =>
      api.post<{ buildId: string }>("/api/v1/artifacts/build", { sourceIds }),
    onSuccess: (data) => {
      setBuildId(data.buildId);
      setBuildPhase("building");
      setBuildError(null);
      setBuiltArtifactId(null);
    },
    onError: (err) => {
      setBuildPhase("failed");
      setBuildError(err instanceof ApiError ? err.message : "Build request failed");
    },
  });

  useEffect(() => {
    if (!buildId || buildPhase !== "building") return;
    const poll = async () => {
      try {
        const status = await api.get<BuildStatus>(`/api/v1/artifacts/build/${buildId}`);
        if (status.state === "done") {
          setBuildPhase("done");
          setBuiltArtifactId(status.artifactId ?? null);
          setSelected(new Set());
          qc.invalidateQueries({ queryKey: ["artifacts"] });
          if (pollRef.current) clearInterval(pollRef.current);
        } else if (status.state === "failed") {
          setBuildPhase("failed");
          setBuildError(status.error ?? "Unknown build error");
          if (pollRef.current) clearInterval(pollRef.current);
        }
      } catch {
        /* network hiccup */
      }
    };
    pollRef.current = setInterval(poll, 5000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [buildId, buildPhase, qc]);

  const empty = sources.data && sources.data.length === 0;
  const filteredSources =
    sources.data?.filter((s) => {
      const term = search.toLowerCase();
      const matchesSearch =
        s.name.toLowerCase().includes(term) || s.connectorId.toLowerCase().includes(term);
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "never" && !s.lastStatus) ||
        (statusFilter !== "never" && s.lastStatus === statusFilter);
      return matchesSearch && matchesStatus;
    }) ?? [];

  const filteredSourceIds = filteredSources.map((s) => s.id);
  const filteredSelectedIds = filteredSourceIds.filter((id) => selected.has(id));
  const filteredSelectedCount = filteredSelectedIds.length;
  const allSelected =
    filteredSourceIds.length > 0 && filteredSelectedCount === filteredSourceIds.length;
  const someSelected = filteredSelectedCount > 0 && !allSelected;

  // Drop selected ids that fall outside the current filter so selection
  // never silently retains hidden rows across search/status changes.
  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(filteredSourceIds);
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [search, statusFilter]);

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) filteredSourceIds.forEach((id) => next.delete(id));
      else filteredSourceIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const bulkRefresh = () => {
    filteredSelectedIds.forEach((id) => refresh.mutate(id));
  };

  const bulkDelete = () => {
    filteredSelectedIds.forEach((id) => remove.mutate(id));
    setConfirmBulkDelete(false);
  };

  const enabledCount = sources.data?.filter((s: SourceRow) => s.enabled).length ?? 0;
  const totalCount = sources.data?.length ?? 0;
  const hasActiveFilters = search !== "" || statusFilter !== "all";

  let buildButtonLabel: string;
  if (buildPhase === "building") buildButtonLabel = "Building…";
  else if (filteredSelectedCount > 0)
    buildButtonLabel = `Build Artifact Bundle (.lca) (${filteredSelectedCount})`;
  else buildButtonLabel = "Build Artifact Bundle (.lca)";

  let sourceContent: React.ReactNode;
  if (sources.isLoading) {
    sourceContent = (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="card h-14 animate-pulseSoft" />
        ))}
      </div>
    );
  } else if (empty) {
    sourceContent = <EmptyState onPickConnector={() => setLocation("/connectors")} />;
  } else {
    sourceContent = (
      <div className="card overflow-hidden">
        <div className="max-h-[calc(100vh-15rem)] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10">
              <tr className="border-b border-rule bg-datahub-gray-1500 text-left">
                <Th className="w-10 px-4">
                  <input
                    type="checkbox"
                    className="cursor-pointer accent-ink"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected;
                    }}
                    onChange={toggleAll}
                    aria-label="Select all sources"
                  />
                </Th>
                <Th>Name</Th>
                <Th className="hidden sm:table-cell">Connector</Th>
                <Th className="hidden md:table-cell">Schedule</Th>
                <Th className="hidden sm:table-cell">Status</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-rule">
              {filteredSources.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-sm text-ash">
                    No sources match the current filters
                  </td>
                </tr>
              ) : (
                filteredSources.map((s) => {
                  const isSelected = selected.has(s.id);
                  return (
                    <tr
                      key={s.id}
                      onClick={() => setLocation(`/sources/${encodeURIComponent(s.id)}`)}
                      className={cn(
                        "cursor-pointer transition-colors hover:bg-paper",
                        isSelected && "bg-paper",
                      )}
                    >
                      <Td className="px-4">
                        <input
                          type="checkbox"
                          className="cursor-pointer accent-ink"
                          checked={isSelected}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleOne(s.id)}
                          aria-label={`Select ${s.name}`}
                        />
                      </Td>
                      <Td>
                        <div className="flex items-center gap-2.5">
                          <div
                            className={cn(
                              "h-2 w-2 shrink-0 rounded-full",
                              s.enabled ? "bg-emerald-500" : "bg-datahub-gray-100",
                            )}
                          />
                          <div>
                            <div className="font-semibold tracking-tight text-ink">{s.name}</div>
                            {!s.enabled && (
                              <div className="text-[10.5px] uppercase tracking-wider text-ash">
                                Paused
                              </div>
                            )}
                          </div>
                        </div>
                      </Td>
                      <Td className="hidden sm:table-cell">
                        <div className="flex items-center gap-1.5">
                          <PlatformLogo
                            platformId={s.connectorId}
                            alt=""
                            className="h-4 w-4 shrink-0"
                          />
                          <span className="rounded border border-rule bg-paper px-1.5 py-0.5 font-mono text-[11px] text-ash">
                            {s.connectorId}
                          </span>
                        </div>
                      </Td>
                      <Td className="hidden md:table-cell">
                        {s.scheduleCron ? (
                          <span className="font-mono text-[11.5px] text-ink">{s.scheduleCron}</span>
                        ) : (
                          <span className="text-[12px] text-ash">On-demand</span>
                        )}
                      </Td>
                      <Td className="hidden sm:table-cell">
                        <StatusBadge status={s.lastStatus} />
                      </Td>
                      <Td className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditing(s);
                            }}
                            className="btn-ghost text-[12px] py-1 px-2"
                          >
                            Edit
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              refresh.mutate(s.id);
                            }}
                            disabled={refresh.isPending && refresh.variables === s.id}
                            className="btn-ghost text-[12px] py-1 px-2"
                          >
                            Refresh
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmDelete(s);
                            }}
                            className="btn-danger text-[12px] py-1 px-2"
                          >
                            Delete
                          </button>
                        </div>
                      </Td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <header className="pt-8 pb-4">
        <div className="eyebrow mb-1">Configured</div>
        <h1 className="font-display text-[28px] sm:text-[32px] font-semibold leading-tight tracking-tight text-ink">
          Sources
        </h1>
        <p className="mt-1.5 max-w-xl text-[13px] sm:text-[13.5px] leading-relaxed text-ash">
          Each source parses and ingests metadata for one target platform. Click <em>Refresh</em> to
          ingest on demand, or open a source for its run history.
        </p>
      </header>

      {/* TOOLBAR */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2 sm:gap-3">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or connector…"
            className="input w-full sm:w-auto sm:flex-1 sm:min-w-[200px] lg:min-w-[260px]"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="input w-auto"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {hasActiveFilters && (
            <button
              onClick={() => {
                setSearch("");
                setStatusFilter("all");
              }}
              className="text-[12px] text-ash hover:text-ink"
            >
              Clear filters ×
            </button>
          )}
        </div>

        {/* Right: counters + actions */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {/* Enabled counter — compact pill on mobile, card on desktop */}
          {totalCount > 0 && (
            <div className="flex items-center gap-1.5 rounded-lg border border-rule bg-surface px-3 py-1.5 sm:px-4 sm:py-2">
              <span className="font-display text-[18px] sm:text-[20px] font-semibold leading-none text-ink">
                {enabledCount}
                <span className="text-ash">/{totalCount}</span>
              </span>
              <span className="font-mono text-[9px] sm:text-[9.5px] uppercase tracking-wider text-ash">
                enabled
              </span>
            </div>
          )}

          <button
            onClick={() => setLocation("/connectors")}
            className="btn-secondary text-[13px] sm:text-sm"
          >
            Browse connectors
          </button>

          {/* Build .lca — hidden label on xs, full label on sm+ */}
          {isLcaMode && !empty && (
            <button
              onClick={() => buildArtifact.mutate(filteredSelectedIds)}
              disabled={filteredSelectedCount === 0 || buildPhase === "building"}
              className="btn-secondary text-[13px] sm:text-sm"
              title={
                filteredSelectedCount === 0
                  ? "Select at least one source to build an artifact"
                  : undefined
              }
            >
              <span className="hidden sm:inline">{buildButtonLabel}</span>
              <span className="sm:hidden">
                {buildPhase === "building"
                  ? "Building…"
                  : `Build${filteredSelectedCount > 0 ? ` (${filteredSelectedCount})` : ""}`}
              </span>
            </button>
          )}

          <button onClick={() => setAdding(true)} className="btn-primary text-[13px] sm:text-sm">
            <span className="text-[15px] leading-none">+</span>
            <span className="hidden xs:inline sm:inline"> Add source</span>
          </button>
        </div>
      </div>

      {/* Build result banners */}
      {buildPhase === "done" && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          <span>Artifact built successfully.</span>
          <button onClick={() => setLocation("/artifacts")} className="font-medium underline">
            View in Artifacts →
          </button>
        </div>
      )}
      {buildPhase === "failed" && buildError && (
        <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          Build failed: {buildError}
        </div>
      )}

      {/* Bulk action bar: shown when one or more visible sources are selected */}
      {filteredSelectedCount > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-rule bg-surface px-4 py-2.5">
          <span className="text-[13px] font-medium text-ink">{filteredSelectedCount} selected</span>
          <div className="flex-1" />
          <button
            onClick={bulkRefresh}
            disabled={refresh.isPending}
            className="btn-secondary text-[12px] py-1 px-2.5"
          >
            Refresh selected
          </button>
          <button
            onClick={() => setConfirmBulkDelete(true)}
            className="btn-danger text-[12px] py-1 px-2.5"
          >
            Delete selected
          </button>
          <button
            onClick={() => setSelected(new Set())}
            className="text-[12px] text-ash hover:text-ink"
          >
            Clear
          </button>
        </div>
      )}

      <section>
        {sourceContent}
        {refresh.error instanceof ApiError && (
          <p className="mt-3 text-sm text-rose-700">{refresh.error.message}</p>
        )}
      </section>

      <AddSourceDialog
        open={adding}
        onOpenChange={setAdding}
        onConnectorSelected={(c) => setConfiguringConnector(c)}
      />

      {configuringConnector && (
        <ConfigureConnectorModal
          open
          onOpenChange={(o) => {
            if (!o) setConfiguringConnector(null);
          }}
          connectorSummary={configuringConnector}
          onPickDifferentConnector={() => {
            setConfiguringConnector(null);
            setAdding(true);
          }}
        />
      )}

      {editing && (
        <EditConnectorModal
          source={editing}
          open
          onOpenChange={(o) => {
            if (!o) setEditing(null);
          }}
        />
      )}
      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete source"
        description={`Delete "${confirmDelete?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={() => {
          if (confirmDelete) remove.mutate(confirmDelete.id);
          setConfirmDelete(null);
        }}
        onCancel={() => setConfirmDelete(null)}
      />
      <ConfirmDialog
        open={confirmBulkDelete}
        title="Delete sources"
        description={`Delete ${filteredSelectedCount} selected source${filteredSelectedCount === 1 ? "" : "s"}? This cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={bulkDelete}
        onCancel={() => setConfirmBulkDelete(false)}
      />
    </div>
  );
}

function Th({ children, className }: Readonly<{ children: React.ReactNode; className?: string }>) {
  return (
    <th
      className={
        "px-5 py-3 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-ash " +
        (className ?? "")
      }
    >
      {children}
    </th>
  );
}

function Td({ children, className }: Readonly<{ children: React.ReactNode; className?: string }>) {
  return <td className={"px-5 py-3.5 align-middle " + (className ?? "")}>{children}</td>;
}

function StatusBadge({ status }: Readonly<{ status?: string }>) {
  if (!status) return <span className="chip-neutral">Never run</span>;
  if (status === "succeeded")
    return (
      <span className="chip-emerald">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Succeeded
      </span>
    );
  if (status === "failed") return <span className="chip-rose">Failed</span>;
  if (status === "cancelled") return <span className="chip-neutral">Cancelled</span>;
  if (status === "running")
    return (
      <span className="chip-amber animate-pulseSoft">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Running
      </span>
    );
  return <span className="chip-neutral">{status}</span>;
}

function EmptyState({ onPickConnector }: Readonly<{ onPickConnector: () => void }>) {
  return (
    <div className="card relative overflow-hidden">
      <div className="absolute inset-0 grid-hero opacity-60" />
      <div className="relative grid place-items-center px-6 py-16 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-md border border-rule bg-surface text-ash">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
            <path
              d="M5 6h14M5 12h14M5 18h9"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </div>
        <h2 className="mt-4 font-display text-[20px] font-semibold tracking-tight text-ink">
          No sources yet
        </h2>
        <p className="mt-2 max-w-sm text-[13.5px] text-ash">
          A source connects the agent to one PLC, SCADA, historian, or database. Pick a connector to
          start.
        </p>
        <button onClick={onPickConnector} className="btn-primary mt-5">
          Browse connectors
        </button>
      </div>
    </div>
  );
}
